// ============================================================================
// FSA DOCUMENT OUTPUT PROFILE SERVICE (ADR-0003)
// ============================================================================
// CRUD over the persisted PDF "house style" profiles. The settings screen edits
// the tenant default (getDefault → update, or create when none exists yet).

import { get, post, put } from '../../client/axiosClient';
import { fsaDocumentOutputEndpoints } from '../../endpoints/fsa/fsaDocumentOutput.endpoints';
import type { ApiResponse, ID } from '../../types';
import type {
  DocumentOutputProfile,
  CreateDocumentOutputProfileRequest,
  UpdateDocumentOutputProfileRequest,
  RegistrationTypeOption,
} from '../../types/fsa/documentOutput.types';

class FsaDocumentOutputProfileService {
  /** The single tenant default profile, or null when none configured (404). */
  async getDefault(): Promise<DocumentOutputProfile | null> {
    try {
      const response = await get<ApiResponse<DocumentOutputProfile>>(fsaDocumentOutputEndpoints.getDefault());
      return response.data?.data ?? null;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  async getById(id: ID): Promise<ApiResponse<DocumentOutputProfile>> {
    const response = await get<ApiResponse<DocumentOutputProfile>>(fsaDocumentOutputEndpoints.byId(id));
    return response.data;
  }

  async getRegistrationTypes(): Promise<RegistrationTypeOption[]> {
    const response = await get<ApiResponse<RegistrationTypeOption[]>>(fsaDocumentOutputEndpoints.registrationTypes());
    return response.data?.data ?? [];
  }

  async create(request: CreateDocumentOutputProfileRequest): Promise<ApiResponse<DocumentOutputProfile>> {
    const response = await post<ApiResponse<DocumentOutputProfile>>(fsaDocumentOutputEndpoints.create(), request);
    return response.data;
  }

  async update(id: ID, request: UpdateDocumentOutputProfileRequest): Promise<ApiResponse<DocumentOutputProfile>> {
    const response = await put<ApiResponse<DocumentOutputProfile>>(fsaDocumentOutputEndpoints.update(id), request);
    return response.data;
  }
}

export const fsaDocumentOutputProfileService = new FsaDocumentOutputProfileService();
